import { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { apiPrefix } from '../../config';
import { getAuthHeader } from '../../helpers/auth';
import { handleRequestError } from '../../helpers/requests';
import RegionSelect from '../common/RegionSelect';
import Toasts from '../common/Toasts';
import CompanyAutocomplete from './CompanyAutocomplete';

interface FormState {
  email: string;
  password: string;
  firstname: string;
  surname: string;
  prefname: string;
  company_position: string;
  phonenumber: string;
  'custom:Company': string;
  'custom:Division': string;
  'custom:ActiveRegion': string;
}

const EMPTY_FORM: FormState = {
  email: '',
  password: '',
  firstname: '',
  surname: '',
  prefname: '',
  company_position: '',
  phonenumber: '',
  'custom:Company': '',
  'custom:Division': '',
  'custom:ActiveRegion': '',
};

const PASSWORD_RULES: { label: string; test: (pw: string) => boolean }[] = [
  { label: 'At least 8 characters long', test: (pw) => pw.length >= 8 },
  { label: 'Contains at least 1 uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
  { label: 'Contains at least 1 lowercase letter', test: (pw) => /[a-z]/.test(pw) },
  { label: 'Contains at least 1 number', test: (pw) => /[0-9]/.test(pw) },
  {
    label: 'Contains at least 1 special character',
    test: (pw) => /[^A-Za-z0-9]/.test(pw),
  },
];

const CreateUser = () => {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [developers, setDevelopers] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDevelopers = async () => {
      const response = await fetch(`${apiPrefix}/developer`, {
        headers: await getAuthHeader(),
      });
      if (response.ok) {
        const data = await response.json();
        const names: string[] = (data.developers ?? [])
          .map((d: { developername: string }) => d.developername)
          .filter((n: string) => n && n.trim() !== '')
          .sort((a: string, b: string) => a.trim().localeCompare(b.trim()));
        setDevelopers(Array.from(new Set(names)));
      } else handleRequestError(response);
    };
    fetchDevelopers();
  }, []);

  const handleField =
    (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleRegionChange = (e: any) =>
    setForm((prev) => ({ ...prev, 'custom:ActiveRegion': e.value ?? '' }));

  const passwordChecks = PASSWORD_RULES.map((rule) => ({
    label: rule.label,
    passed: rule.test(form.password),
  }));
  const passwordValid = passwordChecks.every((c) => c.passed);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const canSubmit = emailValid && passwordValid && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      setError('Email and password are required');
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!passwordValid) {
      setError('Password does not meet all requirements');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setSubmitting(true);
    const body: Record<string, string> = {};
    (Object.keys(form) as (keyof FormState)[]).forEach((key) => {
      if (form[key]) body[key] = form[key];
    });

    const response = await fetch(`${apiPrefix}/cognitouser`, {
      method: 'POST',
      headers: await getAuthHeader({ isJson: true }),
      body: JSON.stringify(body),
    });
    if (response.ok) {
      const data = await response.json();
      setInfo(data.message || 'User created successfully');
      setTimeout(() => setInfo(null), 3000);
      setForm(EMPTY_FORM);
    } else {
      const data = await response.json().catch(() => ({}));
      setError(data.error || 'Failed to create user');
      handleRequestError(response);
      setTimeout(() => setError(null), 5000);
    }
    setSubmitting(false);
  };

  return (
    <Container className="my-4" style={{ position: 'relative' }}>
      <Toasts info={info} error={error} setInfo={setInfo} setError={setError} />
      <Form onSubmit={handleSubmit}>
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={3}>
            Email *
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              type="email"
              value={form.email}
              onChange={handleField('email')}
              required
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={3}>
            Password *
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              type="text"
              value={form.password}
              onChange={handleField('password')}
              required
              isInvalid={form.password !== '' && !passwordValid}
              isValid={form.password !== '' && passwordValid}
            />
            <ul
              className="list-unstyled mt-2 mb-0"
              style={{ fontSize: '0.85rem' }}
            >
              {passwordChecks.map((check) => (
                <li
                  key={check.label}
                  className={check.passed ? 'text-success' : 'text-muted'}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      width: '1.25rem',
                      fontWeight: 700,
                    }}
                  >
                    {check.passed ? '✓' : '✗'}
                  </span>
                  {check.label}
                </li>
              ))}
            </ul>
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={3}>
            First Name
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              type="text"
              value={form.firstname}
              onChange={handleField('firstname')}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={3}>
            Surname
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              type="text"
              value={form.surname}
              onChange={handleField('surname')}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={3}>
            Preferred Name
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              type="text"
              value={form.prefname}
              onChange={handleField('prefname')}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={3}>
            Company Position
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              type="text"
              value={form.company_position}
              onChange={handleField('company_position')}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={3}>
            Phone Number
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              type="tel"
              value={form.phonenumber}
              onChange={handleField('phonenumber')}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={3}>
            Company
          </Form.Label>
          <Col sm={9}>
            <CompanyAutocomplete
              value={form['custom:Company']}
              options={developers}
              onChange={(v) =>
                setForm((prev) => ({ ...prev, 'custom:Company': v }))
              }
              placeholder="Start typing to see suggestions"
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={3}>
            Division
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              type="text"
              value={form['custom:Division']}
              onChange={handleField('custom:Division')}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={3}>
            Active Region
          </Form.Label>
          <Col sm={9}>
            <RegionSelect onChange={handleRegionChange} />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Col sm={{ span: 9, offset: 3 }}>
            <Button type="submit" disabled={!canSubmit}>
              {submitting ? 'Creating...' : 'Create User'}
            </Button>
          </Col>
        </Form.Group>
      </Form>
    </Container>
  );
};

export default CreateUser;
